import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  AdminAccessRequest,
  approveAccessRequest,
  EventAdminPermissionLevel,
  EventAdminUser,
  grantEventAdmin,
  listAccessRequests,
  listEventAdmins,
  revokeEventAdmin,
  searchAdminUsers,
  updateEventAdminPermission,
  rejectAccessRequest,
} from "../../api/admin";
import { listEventsAuth } from "../../api/events";
import LoadingPanel from "../../components/LoadingPanel/LoadingPanel";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import { getUserFacingErrorMessage } from "../../utils/userFacingError";

function permissionLabel(level: EventAdminPermissionLevel): string {
  return level === "full" ? "Full management" : "Registrations only";
}

function pendingRequestTitle(
  req: AdminAccessRequest,
  adminsByEvent: Record<number, EventAdminUser[]>,
): string {
  if (req.requestType === "post_events") {
    return "Post events permission";
  }
  if (req.event?.id && req.user?.id) {
    const admins = adminsByEvent[req.event.id] ?? [];
    const existing = admins.find((a) => a.user.id === req.user!.id);
    if (existing?.permissionLevel === "registrations") {
      return `Full management upgrade — ${req.event.title}`;
    }
  }
  return req.event?.title ?? "Event admin access";
}

function defaultApproveLevel(
  req: AdminAccessRequest,
  adminsByEvent: Record<number, EventAdminUser[]>,
): EventAdminPermissionLevel {
  if (req.requestType === "post_events" || !req.event?.id || !req.user?.id) {
    return "registrations";
  }
  const admins = adminsByEvent[req.event.id] ?? [];
  const existing = admins.find((a) => a.user.id === req.user!.id);
  return existing?.permissionLevel === "registrations" ? "full" : "registrations";
}

export default function SuperAdminEventManagement() {
  const [pending, setPending] = useState<AdminAccessRequest[]>([]);
  const [pendingAdminsByEvent, setPendingAdminsByEvent] = useState<
    Record<number, EventAdminUser[]>
  >({});
  const [approveLevels, setApproveLevels] = useState<
    Record<number, EventAdminPermissionLevel>
  >({});
  const [pendingLoading, setPendingLoading] = useState(true);
  const [pendingError, setPendingError] = useState<string | null>(null);
  const [eventError, setEventError] = useState<string | null>(null);
  const [actionId, setActionId] = useState<number | null>(null);

  const [eventOptions, setEventOptions] = useState<
    { id: number; title: string; status: string }[]
  >([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<number | "">("");
  const [eventAdmins, setEventAdmins] = useState<EventAdminUser[]>([]);
  const [eventAdminsLoading, setEventAdminsLoading] = useState(false);
  const [eventAdminsLoaded, setEventAdminsLoaded] = useState(false);

  const [grantSearch, setGrantSearch] = useState("");
  const [grantUsers, setGrantUsers] = useState<
    { id: number; email: string; firstName: string | null; lastName: string | null }[]
  >([]);
  const [grantUserId, setGrantUserId] = useState<number | "">("");
  const [grantLevel, setGrantLevel] =
    useState<EventAdminPermissionLevel>("registrations");
  const [isGranting, setIsGranting] = useState(false);

  const loadPending = async () => {
    setPendingLoading(true);
    setPendingError(null);
    try {
      const requests = await listAccessRequests("pending");
      const eventIds = Array.from(
        new Set(
          requests
            .filter((r) => r.requestType !== "post_events" && r.event?.id)
            .map((r) => r.event!.id),
        ),
      );

      const adminsByEvent: Record<number, EventAdminUser[]> = {};
      await Promise.all(
        eventIds.map(async (eventId) => {
          adminsByEvent[eventId] = await listEventAdmins(eventId);
        }),
      );

      setPending(requests);
      setPendingAdminsByEvent(adminsByEvent);
      setApproveLevels((prev) => {
        const next = { ...prev };
        for (const req of requests) {
          if (!next[req.id]) {
            next[req.id] = defaultApproveLevel(req, adminsByEvent);
          }
        }
        return next;
      });
    } catch (err) {
      setPendingError(
        getUserFacingErrorMessage(err, "Failed to load pending requests."),
      );
    } finally {
      setPendingLoading(false);
    }
  };

  const loadEventOptions = async () => {
    if (eventsLoaded || eventsLoading) return;
    setEventsLoading(true);
    setEventError(null);
    try {
      const all = await listEventsAuth({ limit: 100 });
      setEventOptions(
        all.map((e) => ({ id: e.id, title: e.title, status: e.status })),
      );
      setEventsLoaded(true);
    } catch (err) {
      setEventError(
        getUserFacingErrorMessage(err, "Failed to load events."),
      );
    } finally {
      setEventsLoading(false);
    }
  };

  const loadEventAdmins = async (eventId: number) => {
    setEventAdminsLoading(true);
    setEventError(null);
    try {
      const admins = await listEventAdmins(eventId);
      setEventAdmins(admins);
      setEventAdminsLoaded(true);
    } catch (err) {
      setEventError(
        getUserFacingErrorMessage(err, "Failed to load administrators."),
      );
      setEventAdmins([]);
      setEventAdminsLoaded(false);
    } finally {
      setEventAdminsLoading(false);
    }
  };

  const handleEventSelect = async (eventId: number | "") => {
    setSelectedEventId(eventId);
    setEventAdmins([]);
    setEventAdminsLoaded(false);
    setGrantSearch("");
    setGrantUsers([]);
    setGrantUserId("");
    setGrantLevel("registrations");
    if (eventId) {
      await loadEventAdmins(eventId);
    }
  };

  useEffect(() => {
    loadPending();
  }, []);

  const handleApprove = async (id: number) => {
    setActionId(id);
    try {
      await approveAccessRequest(id, {
        permissionLevel: approveLevels[id] ?? "registrations",
      });
      await loadPending();
      if (selectedEventId) {
        await loadEventAdmins(selectedEventId);
      }
    } catch (err) {
      setPendingError(
        getUserFacingErrorMessage(err, "Could not approve request."),
      );
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async (id: number) => {
    setActionId(id);
    try {
      await rejectAccessRequest(id);
      await loadPending();
    } catch (err) {
      setPendingError(
        getUserFacingErrorMessage(err, "Could not reject request."),
      );
    } finally {
      setActionId(null);
    }
  };

  const handlePermissionChange = async (
    userId: number,
    permissionLevel: EventAdminPermissionLevel,
  ) => {
    if (!selectedEventId) return;
    try {
      await updateEventAdminPermission(selectedEventId, userId, permissionLevel);
      await loadEventAdmins(selectedEventId);
    } catch (err) {
      setEventError(
        getUserFacingErrorMessage(err, "Could not update permission."),
      );
    }
  };

  const handleRevoke = async (userId: number) => {
    if (!selectedEventId) return;
    if (!window.confirm("Remove this user's access to this event?")) return;
    try {
      await revokeEventAdmin(selectedEventId, userId);
      await loadEventAdmins(selectedEventId);
    } catch (err) {
      setEventError(
        getUserFacingErrorMessage(err, "Could not revoke access."),
      );
    }
  };

  const handleGrantSearch = async () => {
    if (!grantSearch.trim()) return;
    try {
      const users = await searchAdminUsers(grantSearch.trim());
      setGrantUsers(users);
    } catch {
      setGrantUsers([]);
    }
  };

  const handleGrant = async () => {
    if (!selectedEventId || !grantUserId) return;
    setIsGranting(true);
    setEventError(null);
    try {
      await grantEventAdmin(selectedEventId, Number(grantUserId), grantLevel);
      setGrantSearch("");
      setGrantUsers([]);
      setGrantUserId("");
      setGrantLevel("registrations");
      await loadEventAdmins(selectedEventId);
    } catch (err) {
      setEventError(
        getUserFacingErrorMessage(err, "Could not grant access."),
      );
    } finally {
      setIsGranting(false);
    }
  };

  const selectedEvent = eventOptions.find((e) => e.id === selectedEventId);

  return (
    <div>
      <section className="mb-10">
        <h2 className="font-semibold text-lg mb-4">
          Pending admin requests ({pending.length})
        </h2>
        {pendingError && (
          <ErrorAlert className="mb-4">{pendingError}</ErrorAlert>
        )}
        {pendingLoading ? (
          <LoadingPanel
            title="Loading pending requests"
            subtitle="Fetching access requests awaiting review."
            className="py-8"
          />
        ) : pending.length === 0 ? (
          <p className="text-slate-600 text-sm">No pending requests.</p>
        ) : (
          <div className="space-y-3">
            {pending.map((req) => (
              <div
                key={req.id}
                className="border p-4 rounded-sm bg-white text-sm"
              >
                <p className="font-medium">
                  {pendingRequestTitle(req, pendingAdminsByEvent)}
                </p>
                <p className="text-slate-600">
                  {req.user?.firstName} {req.user?.lastName} ({req.user?.email})
                </p>
                {req.message && <p className="mt-2">{req.message}</p>}
                <p className="text-xs text-slate-500 mt-1">
                  Submitted {req.createdAt.slice(0, 10)}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {req.requestType !== "post_events" && (
                    <select
                      className="border border-slate-300 p-1.5 text-xs"
                      aria-label="Approval permission level"
                      value={approveLevels[req.id] ?? "registrations"}
                      onChange={(e) =>
                        setApproveLevels((prev) => ({
                          ...prev,
                          [req.id]: e.target.value as EventAdminPermissionLevel,
                        }))
                      }
                    >
                      <option value="registrations">Registrations only</option>
                      <option value="full">Full management</option>
                    </select>
                  )}
                  <button
                    type="button"
                    className="eh-btn-primary text-xs"
                    disabled={actionId === req.id}
                    onClick={() => handleApprove(req.id)}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="eh-btn-secondary text-xs"
                    disabled={actionId === req.id}
                    onClick={() => handleReject(req.id)}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-semibold text-lg mb-2">Event administrators</h2>
        <p className="text-sm text-slate-600 mb-4">
          Choose an event to manage its administrators and grant access.
        </p>
        {eventError && (
          <ErrorAlert className="mb-4">{eventError}</ErrorAlert>
        )}

        <div className="border border-slate-200 rounded-sm bg-white p-4 mb-6">
          <label className="block text-sm mb-1" htmlFor="admin-event-select">
            Event
          </label>
          <select
            id="admin-event-select"
            className="w-full border border-slate-300 p-2 text-sm"
            value={selectedEventId}
            disabled={eventsLoading}
            onFocus={loadEventOptions}
            onChange={(e) => {
              const value = e.target.value ? Number(e.target.value) : "";
              void handleEventSelect(value);
            }}
          >
            <option value="">
              {eventsLoading ? "Loading events…" : "Select an event…"}
            </option>
            {eventOptions.map((e) => (
              <option key={e.id} value={e.id}>
                {e.title}
                {e.status !== "published" ? ` (${e.status})` : ""}
              </option>
            ))}
          </select>
        </div>

        {selectedEventId && eventAdminsLoading && (
          <LoadingPanel
            title="Loading administrators"
            subtitle="Fetching admins for the selected event."
            className="py-8 mb-6"
          />
        )}

        {selectedEventId && eventAdminsLoaded && (
          <>
            <h3 className="font-medium text-base mb-3">
              {selectedEvent?.title ?? "Event"} — administrators (
              {eventAdmins.length})
            </h3>
            {eventAdmins.length === 0 ? (
              <p className="text-slate-600 text-sm border p-4 rounded-sm bg-white mb-6">
                No administrators for this event yet.
              </p>
            ) : (
              <div className="border rounded-sm overflow-hidden bg-white mb-6">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 text-left">
                    <tr>
                      <th className="p-3">User</th>
                      <th className="p-3">Email</th>
                      <th className="p-3">Permission</th>
                      <th className="p-3">Granted</th>
                      <th className="p-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {eventAdmins.map((admin) => (
                      <tr key={admin.id} className="border-t border-slate-100">
                        <td className="p-3 font-medium">
                          {admin.user.firstName} {admin.user.lastName}
                        </td>
                        <td className="p-3 text-slate-600">{admin.user.email}</td>
                        <td className="p-3">
                          <select
                            className="border border-slate-300 p-1 text-xs"
                            aria-label={`Permission for ${admin.user.email}`}
                            value={admin.permissionLevel ?? "full"}
                            onChange={(e) =>
                              handlePermissionChange(
                                admin.user.id,
                                e.target.value as EventAdminPermissionLevel,
                              )
                            }
                          >
                            <option value="registrations">
                              {permissionLabel("registrations")}
                            </option>
                            <option value="full">
                              {permissionLabel("full")}
                            </option>
                          </select>
                        </td>
                        <td className="p-3 text-slate-600">
                          {admin.grantedAt.slice(0, 10)}
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-3 text-xs">
                            <NavLink
                              to={`/user/event-admin/events/${selectedEventId}`}
                              className="underline"
                            >
                              Panel
                            </NavLink>
                            <button
                              type="button"
                              className="text-red-700 underline"
                              onClick={() => handleRevoke(admin.user.id)}
                            >
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="border border-slate-200 rounded-sm bg-slate-50 p-4">
              <h3 className="font-medium text-base mb-3">Grant new administrator</h3>
              <label className="block text-sm mb-1">Find user</label>
              <div className="flex gap-2 mb-4">
                <input
                  className="flex-1 border border-slate-300 p-2 text-sm bg-white"
                  value={grantSearch}
                  onChange={(e) => setGrantSearch(e.target.value)}
                  placeholder="Search by email or name"
                />
                <button
                  type="button"
                  className="eh-btn-secondary text-sm px-3"
                  onClick={handleGrantSearch}
                >
                  Search
                </button>
              </div>
              <label className="block text-sm mb-1" htmlFor="grant-user-select">
                User
              </label>
              <select
                id="grant-user-select"
                className="w-full border border-slate-300 p-2 mb-4 text-sm bg-white"
                value={grantUserId}
                onChange={(e) =>
                  setGrantUserId(e.target.value ? Number(e.target.value) : "")
                }
              >
                <option value="">Select a user…</option>
                {grantUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName} ({u.email})
                  </option>
                ))}
              </select>
              <label className="block text-sm mb-1" htmlFor="grant-level-select">
                Permission level
              </label>
              <select
                id="grant-level-select"
                className="w-full border border-slate-300 p-2 mb-4 text-sm bg-white"
                value={grantLevel}
                onChange={(e) =>
                  setGrantLevel(e.target.value as EventAdminPermissionLevel)
                }
              >
                <option value="registrations">Registrations only</option>
                <option value="full">Full management</option>
              </select>
              <button
                type="button"
                className="eh-btn-primary text-sm"
                disabled={!grantUserId || isGranting}
                onClick={handleGrant}
              >
                {isGranting ? "Granting…" : "Grant access"}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
